import { useState } from 'react';

interface LocalStorageWarningProps {
  onExportClick?: () => void;
}

/**
 * Warning banner explaining that characters are stored locally.
 * Includes a "Learn More" modal with detailed information.
 */
export function LocalStorageWarning({ onExportClick }: LocalStorageWarningProps) {
  const [showModal, setShowModal] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check if user dismissed this warning before
    return localStorage.getItem('character-library-warning-dismissed') === 'true';
  });

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('character-library-warning-dismissed', 'true');
  };

  if (isDismissed) {
    return null;
  }

  return (
    <>
      {/* Warning Banner */}
      <div className="mb-6 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 text-blue-400">
            <InfoIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-300">
              Characters are saved locally in your browser. Clearing your cache will delete them.{' '}
              <button
                onClick={() => setShowModal(true)}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Learn more
              </button>
            </p>
            <p className="mt-1 text-sm text-slate-400">
              Export your library regularly for backup.{' '}
              {onExportClick && (
                <button
                  onClick={onExportClick}
                  className="text-amber-500 hover:text-amber-400 underline"
                >
                  Export now
                </button>
              )}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-300 rounded"
            aria-label="Dismiss warning"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Learn More Modal */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
            aria-hidden="true"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div
              className="w-full max-w-md bg-slate-800 rounded-xl border border-slate-700 shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-700">
                <h2 id="modal-title" className="text-lg font-semibold text-white">
                  About Local Storage
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 text-slate-400 hover:text-slate-300 rounded"
                  aria-label="Close modal"
                >
                  <CloseIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 space-y-4">
                <div>
                  <h3 className="font-medium text-white mb-1">Where are my characters stored?</h3>
                  <p className="text-sm text-slate-400">
                    Your characters are stored in your browser's IndexedDB, a local database built into modern browsers.
                    This means your data stays on your device and is never sent to any server.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-white mb-1">When might I lose my data?</h3>
                  <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
                    <li>Clearing your browser's cache or site data</li>
                    <li>Using private/incognito browsing mode</li>
                    <li>Browser storage pressure (very rare)</li>
                    <li>Uninstalling your browser</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium text-white mb-1">How do I protect my characters?</h3>
                  <p className="text-sm text-slate-400">
                    Export your character library regularly using the "Export Library" button.
                    This creates a backup file you can import later or on another device.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 pb-6 flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-600 text-slate-300 font-medium hover:bg-slate-700 transition-colors"
                >
                  Got it
                </button>
                {onExportClick && (
                  <button
                    onClick={() => {
                      setShowModal(false);
                      onExportClick();
                    }}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors"
                  >
                    Export Library
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
